import { useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useOrg } from '@/contexts/OrgContext';
import {
  BUILDER_ENTITY_ID_PARAM,
  BUILDER_ENTITY_TYPE_PARAM,
  builderConversationPath,
  useBuilderMode,
} from '@/lib/crm/hooks/useBuilderMode';
import { apiPost } from '@/lib/http/client';
import { trackChatRun } from '@/lib/reasoning/realtime/chatRunTracker';
import { resolveTaskId } from '@/lib/reasoning/realtime/resolveTaskId';

export const useStartNewConversation = (): {
  startNewConversation: (
    prompt: string,
    setError: (error: string) => void,
    fileIds?: number[],
    entityIds?: string[]
  ) => Promise<void>;
} => {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const { isBuilderMode } = useBuilderMode();

  // On the builder route the entity type (and, when editing, the record) ride
  // in the query string, because no conversation exists yet to hold them. This
  // is the handoff: they become columns on the conversation, and from here on
  // every reader takes the mode from the conversation rather than the URL.
  const builderEntityType = searchParams.get(BUILDER_ENTITY_TYPE_PARAM);
  const builderEntityId = searchParams.get(BUILDER_ENTITY_ID_PARAM);

  const startNewConversation = useCallback(
    async (
      prompt: string,
      setError: (error: string) => void,
      fileIds: number[] = [],
      entityIds: string[] = []
    ) => {
      const orgIdValue = orgId || currentOrg?.id;
      if (!orgIdValue) {
        setError('Organization ID not found');
        return;
      }

      try {
        const payload: {
          initial_prompt: string;
          title: string;
          file_ids: number[];
          entity_ids?: string[];
          builder_entity_type?: string;
          builder_entity_id?: string;
        } = {
          initial_prompt: prompt.trim(),
          title: prompt.trim().substring(0, 100),
          file_ids: fileIds,
        };

        const isBuilder = isBuilderMode && Boolean(builderEntityType);
        if (isBuilder && builderEntityType) {
          payload.builder_entity_type = builderEntityType;
          if (builderEntityId) {
            payload.builder_entity_id = builderEntityId;
          }
        }

        // No builder record attached here either. The conversation row is
        // written with its subject before chain 1 is dispatched, so the very
        // first turn grounds from the conversation like every later one — one
        // grounding path, not a special case for the opening message.
        if (entityIds.length > 0) {
          payload.entity_ids = entityIds;
        }

        // conversation_id and chain_id are Django AutoField pks — they arrive
        // as JSON numbers, not strings (apps/reasoning/viewsets/conversations.py).
        const data = await apiPost<{
          conversation_id: number;
          task_id?: string;
          chain_id?: number;
        }>(`/api/orgs/${orgIdValue}/reasoning/conversations/`, payload);

        // The tracker keys runs by conversation id, and the chat page registers
        // its visibility using the route param — always a string. Coerce here so
        // the two agree; a number key never matches, and the completion toast
        // then fires at a user who is already looking at the conversation.
        const conversationId = String(data.conversation_id);

        const newTaskId = resolveTaskId(data);
        if (newTaskId) {
          trackChatRun({
            orgId: String(orgIdValue),
            conversationId,
            taskId: newTaskId,
          });
        }

        // A builder conversation lives under its own path segment. No query
        // params travel: the conversation now carries everything, so a reload
        // or a shared link resolves the same way this navigation does.
        const path = isBuilder
          ? builderConversationPath(orgIdValue, conversationId)
          : `/${orgIdValue}/conversations/${conversationId}`;

        navigate(path, {
          state: {
            taskId: newTaskId ?? undefined,
            fromConversationsHome: true,
            userMessage: prompt,
          },
        });
      } catch (err) {
        console.error('Failed to start conversation', err);
        setError("We couldn't start this conversation. Try again.");
      }
    },
    [
      orgId,
      currentOrg?.id,
      navigate,
      isBuilderMode,
      builderEntityType,
      builderEntityId,
    ]
  );

  return { startNewConversation };
};
